import React from "react";
import Tippy from '@tippyjs/react';
import AsyncSelect from "react-select/async";
import { TextInput } from "@opendate-app/setlist";
import FanFilterSelect from "./FanFilterSelect";
import TimeframeFields from "./TimeframeFields";
import useQueuedFilterParams from "./useQueuedFilterParams";

import { formInputContainerStyle } from "./FanFilterGroupForm";

const PurchaseHistoryForm = (props) => {
  const {
    fanFilter,
    updateFanFilter,
  } = props;

  const conditions = fanFilter.filter_param_options.condition || [];
  const purchasables = fanFilter.filter_param_options.purchasable || [];
  const filterables = fanFilter.filter_param_options.filterables || [];
  const minEventsPurchasables = fanFilter.filter_param_options.min_events_purchasables || [];

  const condition = fanFilter.filter_params.condition || "";
  const purchasable = fanFilter.filter_params.purchasable || "";
  const filterableId = fanFilter.filter_params.filterable_id || null;

  const { update, commitText } = useQueuedFilterParams(fanFilter, updateFanFilter);

  const loadFilterableOptions = (inputValue, callback) => {
    const normalizedInput = (inputValue || "").toLowerCase();
    const options = (filterables || [])
      .filter(filterable => !normalizedInput || (filterable.name || "").toLowerCase().includes(normalizedInput))
      .map(filterable => ({ value: filterable.id, label: filterable.name }));

    callback(options);
  };

  const selectedFilterableOption = (() => {
    if (!filterableId) return null;
    const match = (filterables || []).find(filterable => filterable.id === filterableId);
    return match ? { value: match.id, label: match.name } : null;
  })();

  return (
    <>
      <div style={formInputContainerStyle}>
        <FanFilterSelect
          aria-label="Select a condition"
          placeholder="Select a condition"
          options={conditions.map((cond) => ({ value: cond, label: cond }))}
          value={condition || null}
          onChange={(value) => {
            update({
              condition: value,
              purchasable: "",
              filterable_id: null,
              timeframe: "",
              within_value: "",
              within_unit: "",
              start_date: "",
              end_date: "",
              min_events: "",
            });
          }}
        />
      </div>

      {condition && (
        <div style={formInputContainerStyle}>
          <FanFilterSelect
            aria-label="Select a purchasable"
            placeholder="Select a value"
            options={purchasables.map((purch) => ({ value: purch, label: purch }))}
            value={purchasable || null}
            onChange={(value) => update({
              purchasable: value,
              filterable_id: null,
              ...(minEventsPurchasables.includes(value) ? {} : { min_events: "" }),
            })}
          />
        </div>
      )}

      {condition && purchasable && filterables.length > 0 && (
        <div style={formInputContainerStyle}>
          <AsyncSelect
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            key={purchasable}
            placeholder="Select a value"
            defaultOptions
            value={selectedFilterableOption}
            loadOptions={loadFilterableOptions}
            onChange={(option) => update({ filterable_id: option ? option.value : null })}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                minWidth: 0,
                width: "100%",
                borderRadius: "8px",
                border: filterableId ? "1px solid var(--sl-border-primary, #ccc)" : "1px solid var(--sl-border-error, #FF595E)",
              }),
              menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
              menu: (baseStyles) => ({
                ...baseStyles,
                width: "100%",
              }),
            }}
          />
        </div>
      )}

      {condition && purchasable && (
        <TimeframeFields
          params={fanFilter.filter_params}
          options={fanFilter.filter_param_options}
          resetKey={fanFilter.id}
          placeholder="Any time"
          onSelect={(key, value) => update({ [key]: value })}
          onCommitText={commitText}
          onDate={(key, value) => update({ [key]: value })}
        />
      )}

      {condition === "Has purchased" && purchasable && minEventsPurchasables.includes(purchasable) && (
        <div style={{ ...formInputContainerStyle, width: 140 }}>
          <TextInput
            key={`${fanFilter.id}-min_events`}
            aria-label="Minimum events"
            type="number"
            min="1"
            step="1"
            placeholder="Min. events"
            defaultValue={fanFilter.filter_params.min_events ?? ""}
            onBlur={(e) => commitText("min_events", e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitText("min_events", e.target.value);
            }}
          />
        </div>
      )}

      {fanFilter.filter_validation_errors.length ? (
        <Tippy content={fanFilter.filter_validation_errors.join(" ")}>
          <i className="far fa-circle-exclamation" style={{color: "var(--sl-text-error, #FF595E)" }}/>
        </Tippy>
      ) : null}
    </>
  );
};

export default PurchaseHistoryForm;
